import { useState } from 'react'
import { X, BanIcon, AlertTriangle } from 'lucide-react'
import api from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

interface Props {
  isOpen: boolean
  onClose: () => void
  returnData: {
    id: number
    code: string
    customer_name: string
    test_result?: string
    test_notes?: string
  }
  onRejected: () => void
}

const REJECTION_REASONS = [
  { value: 'damaged_by_customer', label: 'Producto dañado por el cliente' },
  { value: 'incomplete', label: 'Producto incompleto / faltan accesorios' },
  { value: 'not_original', label: 'Producto diferente al enviado' },
  { value: 'signs_of_use', label: 'Signos evidentes de uso' },
  { value: 'out_of_time', label: 'Fuera del plazo de devolución' },
  { value: 'warranty_void', label: 'Garantía anulada por manipulación' },
  { value: 'other', label: 'Otro motivo' }
]

export default function RejectRefundModal({ isOpen, onClose, returnData, onRejected }: Props) {
  const [selectedReason, setSelectedReason] = useState('')
  const [customReason, setCustomReason] = useState('')
  const [internalNotes, setInternalNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const toast = useToast()

  const handleReject = async () => {
    const reason = selectedReason === 'other' ? customReason : REJECTION_REASONS.find(r => r.value === selectedReason)?.label

    if (!reason) {
      toast.error('Error', 'Debes seleccionar o escribir un motivo de rechazo')
      return
    }

    setLoading(true)
    try {
      await api.post(`/returns/admin/${returnData.id}/reject-refund`, {
        rejection_reason: reason,
        internal_notes: internalNotes || undefined
      })

      toast.success('Reembolso rechazado', `Reembolso de ${returnData.code} rechazado`)

      onRejected()
      onClose()
    } catch (error: any) {
      toast.error('Error', error.message || 'Error al rechazar el reembolso')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <BanIcon className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                Rechazar Reembolso
              </h2>
              <p className="text-sm text-slate-500">{returnData.code}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Atencion</p>
                <p className="text-sm text-amber-700">
                  El producto ha sido recibido y evaluado, pero no cumple las condiciones de devolucion.
                  Se notificara al cliente <strong>{returnData.customer_name}</strong> del rechazo del reembolso con el motivo indicado.
                </p>
              </div>
            </div>
          </div>

          {/* Test result info */}
          {returnData.test_result && returnData.test_result !== 'ok' && (
            <div className="bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-xl p-4">
              <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Resultado del testeo</p>
              <p className="text-sm text-slate-800 dark:text-slate-200 mt-1 capitalize">{returnData.test_result?.replace(/_/g, ' ')}</p>
              {returnData.test_notes && (
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{returnData.test_notes}</p>
              )}
            </div>
          )}

          {/* Motivo de rechazo */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Motivo del rechazo del reembolso *
            </label>
            <div className="space-y-2">
              {REJECTION_REASONS.map((reason) => (
                <label
                  key={reason.value}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedReason === reason.value
                      ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                      : 'border-slate-200 dark:border-slate-600 hover:border-red-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="refund_rejection_reason"
                    value={reason.value}
                    checked={selectedReason === reason.value}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="w-4 h-4 text-red-600"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    {reason.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Motivo personalizado */}
          {selectedReason === 'other' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Especifica el motivo *
              </label>
              <textarea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Describe el motivo del rechazo del reembolso..."
              />
            </div>
          )}

          {/* Notas internas */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Notas internas (opcional)
            </label>
            <textarea
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              rows={2}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-slate-500 focus:border-transparent"
              placeholder="Notas visibles solo para el equipo..."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            Cancelar
          </button>
          <button
            onClick={handleReject}
            disabled={loading || !selectedReason || (selectedReason === 'other' && !customReason.trim())}
            className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Rechazando...
              </>
            ) : (
              <>
                <BanIcon className="w-4 h-4" />
                Rechazar Reembolso
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
